<template>
    <Tooltip :placement="props.defaultTooltipPlacement">
        <template #trigger="tooltipProps">
            <div
                class="flex flex-col gap-0.5 hover:bg-(--color-200) p-1 rounded-full cursor-help focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-700"
                v-bind="tooltipProps"
                tabindex="0"
                :style="colorStyle"
            >
                <template v-for="level in props.levels" :key="level.level">
                    <div
                        v-if="level.status === 'Occupied'"
                        class="bg-(--color-700) rounded-full"
                        :class="sizeLocationDotClass"
                    />
                    <PartiallyOccupiedIcon
                        v-if="level.status === 'Partially-Occupied'"
                        class="text-(--color-700)"
                        :class="sizeLocationDotClass"
                    />
                    <OptionallyOccupiedIcon
                        v-if="level.status === 'Optionally-Occupied'"
                        class="text-(--color-700)"
                        :class="sizeLocationDotClass"
                    />
                    <div
                        v-if="level.status === 'Empty'"
                        class="border border-gray-500 rounded-full"
                        :class="sizeLocationDotClass"
                    />
                    <div v-if="level.status === 'Placeholder'" :class="sizeLocationDotClass" />
                    <FlashIcon
                        v-if="level.status === 'Open-Conflicts'"
                        class="text-red-600"
                        :class="sizeLocationDotClass"
                    />
                    <FlashOffIcon
                        v-if="level.status === 'Resolved-Conflicts'"
                        class="text-(--color-700)"
                        :class="sizeLocationDotClass"
                    />
                </template>
            </div>
        </template>

        <template #text>
            <div class="flex flex-col" :style="colorStyle">
                <div
                    class="flex flex-row items-center gap-1 mx-auto border-b pb-0.5 border-gray-300 w-full justify-center"
                >
                    <div class="flex flex-row items-center">
                        <i18n path="exhibition-area-count-in-building" :no-tag="true">
                            <template #occupiedExhibitionAreaCount>
                                <div class="font-semibold flex flex-row items-center gap-0.5">
                                    <span class="text-gray-700 font-semibold tabular-nums">
                                        {{ props.occupiedExhibitionAreaCount }}
                                    </span>
                                    <ExhibitionAreaIcon class="text-gray-500" />
                                </div>
                            </template>

                            <template #buildingName>
                                <div class="flex flex-row items-center gap-0.5 ml-1">
                                    <BuildingIcon class="text-gray-500" />
                                    <span class="text-gray-700 font-semibold">{{ props.buildingName }}</span>
                                </div>
                            </template>
                        </i18n>
                    </div>
                </div>

                <div class="pt-0.5">
                    <template v-for="level in props.levels" :key="level.level">
                        <div class="flex flex-row items-center gap-1">
                            <span v-if="level.status !== 'Placeholder'" class="text-gray-500 tabular-nums">
                                .{{ level.level }}
                            </span>
                            <span v-else class="font-light text-gray-500">.</span>
                            <template v-if="level.status !== 'Placeholder'">
                                <ExhibitionAreaIcon />
                                <span class="text-gray-500 font-light leading-none mb-px">→</span>
                            </template>
                            <div v-else class="h-5" />

                            <div v-if="level.status === 'Occupied'" class="flex flex-row items-center gap-1">
                                <div class="bg-(--color-700) rounded-full" :class="sizeLocationDotClass" />
                                <div class="text-gray-700 font-semibold">{{ $t('occupied') }}</div>
                            </div>

                            <div v-if="level.status === 'Partially-Occupied'" class="flex flex-row items-center gap-1">
                                <PartiallyOccupiedIcon class="text-(--color-700)" :class="sizeLocationDotClass" />
                                <div class="text-gray-700 font-semibold">{{ $t('partially-occupied') }}</div>
                            </div>

                            <div v-if="level.status === 'Optionally-Occupied'" class="flex flex-row items-center gap-1">
                                <OptionallyOccupiedIcon class="text-(--color-700)" :class="sizeLocationDotClass" />
                                <div class="text-gray-700 font-semibold">{{ $t('optionally-occupied') }}</div>
                            </div>

                            <div v-if="level.status === 'Empty'" class="flex flex-row items-center gap-1">
                                <div class="border border-gray-500 rounded-full" :class="sizeLocationDotClass" />
                                <div class="text-gray-500 font-light">{{ $t('not-occupied') }}</div>
                            </div>

                            <div v-if="level.status === 'Placeholder'" class="flex flex-row items-center gap-1">
                                <div class="h-2" :class="sizeLocationDotClass" />
                            </div>

                            <div v-if="level.status === 'Open-Conflicts'" class="flex flex-row items-center gap-1">
                                <FlashIcon class="text-red-600" :class="sizeLocationDotClass" />
                                <div class="text-gray-700 font-semibold">{{ $t('open-conflicts') }}</div>
                            </div>

                            <div v-if="level.status === 'Resolved-Conflicts'" class="flex flex-row items-center gap-1">
                                <FlashOffIcon class="text-(--color-700)" :class="sizeLocationDotClass" />
                                <div class="text-gray-700 font-semibold">{{ $t('resolved-conflicts') }}</div>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </template>
    </Tooltip>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import BuildingIcon from '../Icon/BuildingIcon.vue';
import ExhibitionAreaIcon from '../Icon/ExhibitionAreaIcon.vue';
import FlashIcon from '../Icon/FlashIcon.vue';
import FlashOffIcon from '../Icon/FlashOffIcon.vue';
import OptionallyOccupiedIcon from '../Icon/OptionallyOccupiedIcon.vue';
import PartiallyOccupiedIcon from '../Icon/PartiallyOccupiedIcon.vue';
import { get700ColorForPhaseType, getAllocationColor200ForPhaseType } from '../Timeline/phaseColors';
import { PhaseColor } from '../Timeline/Timeline.types';
import Tooltip from '../Tooltip/Tooltip.vue';

export type LocationDotStatus =
    | 'Occupied'
    | 'Partially-Occupied'
    | 'Optionally-Occupied'
    | 'Empty'
    | 'Placeholder'
    // TODO | 'Not-Yet-Build'
    // TODO | 'Demolished'
    | 'Open-Conflicts'
    | 'Resolved-Conflicts';

export type LocationDot = {
    status: LocationDotStatus;
    level: number;
};

type LocationDotsBuildingProps = {
    buildingName: string;
    occupiedExhibitionAreaCount: number;
    levels: LocationDot[];
    defaultTooltipPlacement: 'top' | 'bottom';
    color: Extract<PhaseColor, 'Event' | 'Project'>;
};

const props = defineProps<LocationDotsBuildingProps>();

const sizeLocationDotClass = 'size-2.5';

const colorStyle = computed(() => {
    const color700 = get700ColorForPhaseType(props.color);
    const color200 = getAllocationColor200ForPhaseType(props.color);

    return {
        '--color-700': `var(${color700})`,
        '--color-200': `var(${color200})`,
    };
});
</script>
